import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Subscription } from '@/hooks/useSubscription';

interface SubscriptionCardProps {
  subscription: Subscription | null;
  isSubscribed: boolean;
}

export function SubscriptionCard({ subscription, isSubscribed }: SubscriptionCardProps) {
  if (!isSubscribed || !subscription) {
    return (
      <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <h4 className="font-medium text-foreground">Alpha with AI Club</h4>
            <p className="text-sm text-muted-foreground">Free member</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Premium adds the AI research workflows, monthly consultations and the full archive.
        </p>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <Link to="/ai-investing-club">
            See Premium <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </Card>
    );
  }

  const validUntil = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const endingSoon = subscription.cancel_at_period_end === true;

  const raw = (subscription.plan_name || '').toLowerCase();
  const displayName = raw.includes('founding')
    ? 'Alpha with AI Club Founding'
    : raw.startsWith('alpha with ai')
      ? subscription.plan_name!
      : 'Alpha with AI Club Premium';

  return (
    <Card className="bg-accent/5 border-accent/20 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/30 flex items-center justify-center">
          <CreditCard className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h4 className="font-medium text-foreground">{displayName}</h4>
          <span className="text-xs px-2 py-0.5 rounded-lg bg-accent/10 text-accent capitalize">
            {subscription.plan_type}
          </span>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-accent">●</span>
          <span>
            Status:{' '}
            <span className="text-accent font-medium capitalize">
              {endingSoon ? 'Active (ending soon)' : subscription.status}
            </span>
          </span>
        </div>
        {validUntil && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{endingSoon ? `Ends on ${validUntil}` : `Renews on ${validUntil}`}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
